import { useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MapPin, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { adminOrderApi } from '@/api/admin-order'
import { citiesApi } from '@/api/cities'
import { useOrderCreateStore } from '@/stores/order-create'
import type { ManualAddress } from '@/stores/order-create'
import type { CityAreaOption } from '@/types/city'

const emptyManualAddress: ManualAddress = {
  real_name: '',
  phone: '',
  province: '',
  city: '',
  district: '',
  city_id: 0,
  detail: '',
  save_as_common: false,
}

export function AddressStep() {
  const {
    selectedUser, selectedAddress, setSelectedAddress,
    manualAddress, setManualAddress,
    useManualAddress, setUseManualAddress,
    nextStep, prevStep,
  } = useOrderCreateStore()

  // 获取用户地址列表
  const { data: addresses = [], isLoading } = useQuery({
    queryKey: ['admin', 'orders', 'addresses', selectedUser?.uid],
    queryFn: () => adminOrderApi.listAddresses(selectedUser!.uid),
    enabled: selectedUser !== null,
  })

  const { data: provinces = [] } = useQuery({
    queryKey: ['cities', 0],
    queryFn: () => citiesApi.list(0),
  })

  const selectedProvince = provinces.find((item) => item.name === manualAddress?.province)

  const { data: cities = [] } = useQuery({
    queryKey: ['cities', selectedProvince?.id || 0],
    queryFn: () => citiesApi.list(selectedProvince!.id),
    enabled: useManualAddress && !!selectedProvince,
  })

  const selectedCity = cities.find((item) => item.name === manualAddress?.city)

  const { data: districts = [] } = useQuery({
    queryKey: ['cities', selectedCity?.id || 0],
    queryFn: () => citiesApi.list(selectedCity!.id),
    enabled: useManualAddress && !!selectedCity,
  })

  // 默认选择第一个地址或默认地址
  useEffect(() => {
    if (addresses.length > 0 && !selectedAddress && !useManualAddress) {
      const defaultAddr = addresses.find((a) => a.is_default === 1) || addresses[0]
      setSelectedAddress(defaultAddr)
    }
  }, [addresses, selectedAddress, useManualAddress, setSelectedAddress])

  const handleToggleManual = useCallback((checked: boolean) => {
    setUseManualAddress(checked)
    if (checked) {
      setSelectedAddress(null)
    } else if (addresses.length > 0) {
      const defaultAddr = addresses.find((a) => a.is_default === 1) || addresses[0]
      setSelectedAddress(defaultAddr)
    }
  }, [setUseManualAddress, setSelectedAddress, addresses])

  const handleManualChange = useCallback((field: keyof ManualAddress, value: string | number | boolean) => {
    setManualAddress({
      ...(manualAddress || emptyManualAddress),
      [field]: value,
    })
  }, [manualAddress, setManualAddress])

  const handleProvinceChange = useCallback((value: string) => {
    setManualAddress({
      ...(manualAddress || emptyManualAddress),
      province: value,
      city: '',
      district: '',
      city_id: 0,
    })
  }, [manualAddress, setManualAddress])

  const handleCityChange = useCallback((value: string) => {
    setManualAddress({
      ...(manualAddress || emptyManualAddress),
      city: value,
      district: '',
      city_id: 0,
    })
  }, [manualAddress, setManualAddress])

  const handleDistrictChange = useCallback((value: string) => {
    const district = districts.find((item: CityAreaOption) => item.name === value)
    setManualAddress({
      ...(manualAddress || emptyManualAddress),
      district: value,
      city_id: district?.id || 0,
    })
  }, [districts, manualAddress, setManualAddress])

  const canProceed = useManualAddress
    ? manualAddress?.real_name && manualAddress?.phone && manualAddress?.province && manualAddress?.city && manualAddress?.district && manualAddress?.city_id && manualAddress?.detail
    : selectedAddress !== null

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">收货地址</h3>
        <div className="flex items-center gap-2">
          <Label htmlFor="manual-toggle" className="text-sm text-muted-foreground">
            手动输入地址
          </Label>
          <Switch
            id="manual-toggle"
            checked={useManualAddress}
            onCheckedChange={handleToggleManual}
          />
        </div>
      </div>

      {/* 已有地址列表 */}
      {!useManualAddress && (
        <>
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">加载地址中...</p>
          ) : addresses.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">该用户暂无收货地址，请手动输入</p>
          ) : (
            <RadioGroup
              value={selectedAddress?.id?.toString() || ''}
              onValueChange={(val) => {
                const addr = addresses.find((a) => a.id.toString() === val)
                if (addr) setSelectedAddress(addr)
              }}
              className="space-y-2"
            >
              {addresses.map((addr) => (
                <Card
                  key={addr.id}
                  className={`cursor-pointer transition-colors ${
                    selectedAddress?.id === addr.id ? 'border-primary' : ''
                  }`}
                  onClick={() => setSelectedAddress(addr)}
                >
                  <CardContent className="flex items-start gap-3 py-3">
                    <RadioGroupItem value={addr.id.toString()} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5" />
                        <span className="font-medium">{addr.real_name}</span>
                        <span className="text-muted-foreground">{addr.phone}</span>
                        {addr.is_default === 1 && (
                          <Badge variant="secondary" className="text-xs">默认</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{addr.province}{addr.city}{addr.district}{addr.detail}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </RadioGroup>
          )}
        </>
      )}

      {/* 手动输入地址 */}
      {useManualAddress && (
        <Card>
          <CardContent className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="real_name">收货人</Label>
                <Input
                  id="real_name"
                  placeholder="收货人姓名"
                  value={manualAddress?.real_name || ''}
                  onChange={(e) => handleManualChange('real_name', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">手机号</Label>
                <Input
                  id="phone"
                  placeholder="收货人手机号"
                  value={manualAddress?.phone || ''}
                  onChange={(e) => handleManualChange('phone', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>省市区</Label>
              <div className="grid grid-cols-3 gap-3">
                <Select value={manualAddress?.province || ''} onValueChange={handleProvinceChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="选择省份" />
                  </SelectTrigger>
                  <SelectContent>
                    {provinces.map((province) => (
                      <SelectItem key={province.id} value={province.name}>
                        {province.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={manualAddress?.city || ''}
                  onValueChange={handleCityChange}
                  disabled={!manualAddress?.province}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择城市" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city.id} value={city.name}>
                        {city.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={manualAddress?.district || ''}
                  onValueChange={handleDistrictChange}
                  disabled={!manualAddress?.city}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择区县" />
                  </SelectTrigger>
                  <SelectContent>
                    {districts.map((district) => (
                      <SelectItem key={district.id} value={district.name}>
                        {district.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="detail">详细地址</Label>
              <Input
                id="detail"
                placeholder="街道、门牌号等"
                value={manualAddress?.detail || ''}
                onChange={(e) => handleManualChange('detail', e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="save_as_common"
                checked={manualAddress?.save_as_common || false}
                onCheckedChange={(checked) => handleManualChange('save_as_common', checked === true)}
              />
              <Label htmlFor="save_as_common" className="cursor-pointer text-sm font-normal">
                添加为常用收货地址
              </Label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 底部操作栏 */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={prevStep}>上一步</Button>
        <Button onClick={nextStep} disabled={!canProceed}>
          下一步
        </Button>
      </div>
    </div>
  )
}
